import {
  MenuDivider,
  MenuItem,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import React, { useEffect } from "react";
import { FiEdit2 } from "react-icons/fi";
import { IoArrowRedoOutline, IoTrashBin } from "react-icons/io5";
import { useLocation, useNavigate } from "react-router-dom";

import {
  errorToast,
  IconButton,
  successToast,
  useToast,
} from "../../../components";
import { useIsSmallScreen } from "../../../hooks/useIsSmallScreen";
import { canManageTraining } from "../../../utils/permissions";
import AddToTrainingProgramIcon from "../../components/AddToTrainingProgramModal/AddToTrainingProgramIcon";
import AddToTrainingProgramModal from "../../components/AddToTrainingProgramModal/AddToTrainingProgramModal";
import EditClipModal from "../../components/Clips/EditClipModal";
import ShareClipButton from "../../components/Clips/ShareClipButton";
import useDeleteClip from "../../components/Clips/useDeleteClip";
import { menuItemProps } from "../../components/InterviewDetails/useCallMenuOptions";
import { PlaylistHeader } from "../../components/PlaylistHeader";
import { DetailsHeader } from "../../components/Recording";
import useVisitorAnalytics from "../../components/Recording/useVisitorAnalytics";
import {
  CallSpeakerFragment,
  Candidate,
  Clip,
  ExternalUser,
  Position,
  TranscriptionStatus,
  User,
  useViewClipMutation,
} from "../../graphql";
import { usePlaylist } from "../../graphql/hooks/usePlaylist";
import useCurrentUser from "../../hooks/useCurrentUser";
import useMouseHasMoved from "../../hooks/useMouseHasMoved";
import ClipPill from "./ClipPill";

type ClipHeaderProps = {
  clip:
    | Pick<
        Clip,
        | "id"
        | "name"
        | "displayName"
        | "callStartTime"
        | "canEdit"
        | "canDelete"
        | "canShare"
        | "canShareExternal"
        | "visibility"
        | "visibleVisibilityLevels"
        | "startTime"
        | "endTime"
        | "interviewerIds"
      > & {
        candidate?: Pick<Candidate, "id" | "fullName"> | null;
        position?: Pick<Position, "id" | "displayTitle"> | null;
        interviewers: Pick<User, "id" | "fullName">[];
        viewers: Pick<
          User,
          | "id"
          | "firstName"
          | "lastName"
          | "fullName"
          | "profilePicUrl"
          | "email"
        >[];
        externalViewers: Pick<ExternalUser, "id" | "email">[];
        speakers: CallSpeakerFragment[];
      };
  callId: string;
};

const ClipHeader: React.FC<ClipHeaderProps> = ({ clip, callId }) => {
  const isSmallScreen = useIsSmallScreen();
  const currentUser = useCurrentUser();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const playlistData = usePlaylist();

  const [viewClip, { called: clipViewLogged }] = useViewClipMutation();
  const mouseHasMoved = useMouseHasMoved();
  useEffect(() => {
    if (clip && mouseHasMoved && !clipViewLogged) {
      viewClip({
        variables: { clipId: clip.id, playlistId: playlistData?.playlist?.id },
      });
    }
  }, [clip, mouseHasMoved, clipViewLogged]);

  useVisitorAnalytics({
    canLogCallView: clipViewLogged,
    application: "app",
    callId,
    clipId: clip.id,
    transcriptionStatus: TranscriptionStatus.Completed,
    interviewerIds: clip?.interviewerIds,
  });

  const setPath = (path: string): void =>
    navigate(
      {
        pathname: `/interview/${callId}/clip/${clip.id}${path}`,
        search: window.location.search,
      },
      { replace: true }
    );

  const shareModal = useDisclosure({
    isOpen: location.pathname.endsWith("/share"),
    onOpen: () => setPath("/share"),
    onClose: () => setPath(""),
  });

  const editTitle = useDisclosure();
  const addToTraining = useDisclosure();

  const { openConfirmationDialog, confirmDeleteClipDialog } = useDeleteClip({
    clipId: clip.id,
    onSuccess: () => {
      successToast(toast, "Clip deleted");
      navigate({ pathname: `/interview/${callId}` }, { replace: true });
    },
    onError: (errorMsg) => errorToast(toast, errorMsg),
  });

  const menuOptions = [
    canManageTraining(currentUser) ? (
      <MenuItem
        {...menuItemProps()}
        icon={<AddToTrainingProgramIcon boxSize="5" />}
        onClick={() => addToTraining.onOpen()}
      >
        Add to Training Program
        {addToTraining.isOpen && (
          <AddToTrainingProgramModal
            callId={callId}
            clipIds={[clip.id]}
            onClose={addToTraining.onClose}
          />
        )}
      </MenuItem>
    ) : null,

    clip.canEdit ? (
      <MenuItem
        {...menuItemProps()}
        icon={<FiEdit2 size="18" />}
        onClick={editTitle.onOpen}
      >
        Edit Title
      </MenuItem>
    ) : null,

    <MenuDivider />,

    <Tooltip
      openDelay={500}
      label={
        clip.canDelete
          ? undefined
          : "Please contact clip creator to request deletion of this clip"
      }
    >
      <MenuItem
        {...menuItemProps({ dangerous: true })}
        icon={<IoTrashBin size="20" />}
        onClick={clip.canDelete ? openConfirmationDialog : undefined}
        disabled={!clip.canDelete}
        cursor={clip.canDelete ? undefined : "not-allowed"}
        opacity={clip.canDelete ? undefined : 0.6}
      >
        Delete Clip
        {confirmDeleteClipDialog}
      </MenuItem>
    </Tooltip>,
  ].filter((o) => o !== null);

  return (
    <>
      {playlistData !== null && (
        <PlaylistHeader {...playlistData} px="pagePaddingMobile" />
      )}
      <DetailsHeader
        candidate={clip.candidate}
        position={clip.position}
        titleSlot={
          <ClipPill
            size={isSmallScreen ? "xs" : "sm"}
            highContrast
            display="flex"
          />
        }
        title={clip.displayName}
        interviewers={clip.interviewers}
        date={clip.callStartTime}
        viewers={clip.viewers}
        canEdit={false}
        onEdit={editTitle.onOpen}
        externalViewers={clip.externalViewers}
        menuOptions={menuOptions}
        share={
          clip.canShare || clip.canShareExternal ? (
            <ShareClipButton
              clip={clip}
              callId={callId}
              positionId={clip.position?.id}
              isModalOpen={shareModal.isOpen}
              onOpenModal={shareModal.onOpen}
              onCloseModal={shareModal.onClose}
            >
              <Tooltip label="Share Clip" openDelay={1000}>
                <IconButton
                  icon={<IoArrowRedoOutline size="20" />}
                  aria-label="Share Clip"
                  data-testid="share-button"
                  color="blue.600"
                  variant="white"
                  minW="8"
                  h="8"
                  ml="2"
                />
              </Tooltip>
            </ShareClipButton>
          ) : undefined
        }
      />

      <EditClipModal clip={clip} {...editTitle} />
    </>
  );
};

export default ClipHeader;
